import AndroidOutlinedIcon from '@mui/icons-material/AndroidOutlined'
import SendIcon from '@mui/icons-material/Send'
import {
  Box,
  Chip,
  CircularProgress,
  Drawer,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import { useEffect, useRef, useState } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { api } from '@/api/client'

type Answer = { text: string; suggestions: string[] }
type Turn = { who: 'you' | 'it'; text: string }

/**
 * The assistant, in a drawer off the top bar.
 *
 * <p>It answers from this business's own books and within the asker's own
 * permissions - both decided on the server, because a chat window that decides
 * for itself what somebody may see is a hole in every permission in the
 * system. What it offers to answer is filtered the same way, so nothing it
 * suggests leads to a refusal.</p>
 */
export function Assistant() {
  const [open, setOpen] = useState(false)
  const [text, setText] = useState('')
  const [turns, setTurns] = useState<Turn[]>([])
  const bottom = useRef<HTMLDivElement | null>(null)

  const { data: opening } = useQuery({
    queryKey: ['assistant', 'suggestions'] as const,
    queryFn: () => api.get<Answer>('/api/v1/assistant/suggestions'),
    enabled: open,
  })

  const ask = useMutation({
    mutationFn: (question: string) => api.post<Answer>('/api/v1/assistant/ask', { text: question }),
    onSuccess: (answer) => setTurns((current) => [...current, { who: 'it', text: answer.text }]),
    onError: () =>
      setTurns((current) => [
        ...current,
        { who: 'it', text: 'Something went wrong reaching the assistant. Try again in a moment.' },
      ]),
  })

  useEffect(() => {
    bottom.current?.scrollIntoView({ behavior: 'smooth' })
  }, [turns.length, ask.isPending])

  const send = (question: string) => {
    const asked = question.trim()
    if (!asked || ask.isPending) return
    setTurns((current) => [...current, { who: 'you', text: asked }])
    setText('')
    ask.mutate(asked)
  }

  return (
    <>
      <Tooltip title="Ask the assistant">
        <IconButton color="inherit" onClick={() => setOpen(true)} aria-label="Ask the assistant">
          <AndroidOutlinedIcon />
        </IconButton>
      </Tooltip>

      <Drawer anchor="right" open={open} onClose={() => setOpen(false)}>
        <Stack sx={{ width: { xs: '100vw', sm: 400 }, height: '100%' }}>
          <Box sx={{ px: 2, py: 1.5, borderBottom: 1, borderColor: 'divider' }}>
            <Typography variant="subtitle1">Assistant</Typography>
            <Typography variant="caption" color="text.secondary">
              Answers from your own books, within your own permissions
            </Typography>
          </Box>

          <Stack spacing={1.5} sx={{ flex: 1, overflowY: 'auto', p: 2 }}>
            {turns.length === 0 && opening ? (
              <Typography variant="body2" color="text.secondary">
                {opening.text}
              </Typography>
            ) : null}

            {turns.map((turn, index) => (
              <Box
                key={index}
                sx={{
                  alignSelf: turn.who === 'you' ? 'flex-end' : 'flex-start',
                  maxWidth: '90%',
                  px: 1.5,
                  py: 1,
                  borderRadius: 2,
                  bgcolor: turn.who === 'you' ? 'primary.main' : 'action.hover',
                  color: turn.who === 'you' ? 'primary.contrastText' : 'text.primary',
                }}
              >
                {/* The answers come back with line breaks in them, which is
                    how a list of events is readable at all. */}
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                  {turn.text}
                </Typography>
              </Box>
            ))}

            {ask.isPending ? <CircularProgress size={18} sx={{ alignSelf: 'flex-start' }} /> : null}
            <div ref={bottom} />
          </Stack>

          {/* Only what this person can actually be answered, so nothing
              offered here leads to a refusal. */}
          {opening?.suggestions?.length ? (
            <Stack
              direction="row"
              spacing={1}
              sx={{ px: 2, pb: 1, flexWrap: 'wrap', gap: 1 }}
              useFlexGap
            >
              {opening.suggestions.map((suggestion) => (
                <Chip
                  key={suggestion}
                  size="small"
                  variant="outlined"
                  label={suggestion}
                  onClick={() => send(suggestion)}
                />
              ))}
            </Stack>
          ) : null}

          <Stack direction="row" spacing={1} sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
            <TextField
              fullWidth
              size="small"
              placeholder="Ask about events, money, or an order number"
              value={text}
              onChange={(event) => setText(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') send(text)
              }}
            />
            <IconButton color="primary" onClick={() => send(text)} disabled={ask.isPending}>
              <SendIcon />
            </IconButton>
          </Stack>
        </Stack>
      </Drawer>
    </>
  )
}
